import React from 'react';
import { Box, Divider, Skeleton, Stack, useMediaQuery, useTheme } from '@mui/material';
import Card from '../components/Card';
import ActionBox from './components/ActionBox';
import DonutChart, { DonutChartMobileView } from './components/DonutChart';
import SummaryFooter from './components/SummaryFooter';
import SummaryHeader from './components/SummaryHeader';
import SummaryText from './components/SummaryText';
import { BORDER_COLOR, MAIN_BOX_SHADOW } from 'utils/theme';

const TextSkeleton = () => (
  <Stack gap={1.5}>
    <Skeleton sx={{ width: '40%' }} />
    {Array.from({ length: 8 }).map((_, index) => (
      <Skeleton key={index} />
    ))}
    <Skeleton width="55%" />
    <Stack direction="row" gap={2} mt={2}>
      <Skeleton variant="rounded" height="26px" width="26px" />
      <Skeleton variant="rounded" height="26px" width="26px" />
    </Stack>
  </Stack>
);

interface SummaryProps {
  isLoading: boolean;
  claim: Claim;
  useCardWrapper?: boolean;
  setExpandedClaim?: (value: string | null) => void;
}

const Summary: React.FC<SummaryProps> = ({
  isLoading,
  claim,
  useCardWrapper = true,
  setExpandedClaim,
}) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('tablet'));

  const content = (
    <Stack divider={<Divider />}>
      <Stack
        direction="row"
        pb={3}
        divider={<Divider orientation="vertical" flexItem />}
        sx={{ gap: { mobile: 2, tablet: 2, desktop: 6 } }}
      >
        <Box flex={1.25}>
          {isLoading ? (
            <TextSkeleton />
          ) : (
            <Stack>
              <SummaryHeader />
              <SummaryText summary={claim.summary ?? []} />
              <SummaryFooter claim={claim} />
            </Stack>
          )}
        </Box>
        {!isMobileScreen && (
          <Box flex={1} alignSelf="center">
            <DonutChart
              isLoading={isLoading}
              evidence={claim?.evidence ?? []}
              setExpandedClaim={setExpandedClaim}
              claimId={claim.claim}
            />
          </Box>
        )}
      </Stack>

      <Box p={2} pb={0}>
        <ActionBox isLoading={isLoading} />
      </Box>
    </Stack>
  );

  return (
    <>
      {isMobileScreen && (
        <DonutChartMobileView
          isLoading={isLoading}
          evidence={claim.evidence ?? []}
          setExpandedClaim={setExpandedClaim}
          claimId={claim.claim}
        />
      )}

      {useCardWrapper ? (
        <Card
          sx={{
            paddingX: { mobile: '16px', tablet: '30px', desktop: '30px' },
            paddingY: { mobile: '16px', tablet: '25px', desktop: '25px' },
          }}
        >
          {content}
        </Card>
      ) : (
        <Box
          sx={{
            paddingX: { mobile: '16px', tablet: '30px', desktop: '30px' },
            paddingY: { mobile: '16px', tablet: '25px', desktop: '25px' },
            border: `1px solid ${BORDER_COLOR}`,
            borderBottomLeftRadius: '12px',
            borderBottomRightRadius: '12px',
            borderTopLeftRadius: isMobileScreen ? '12px' : 0,
            borderTopRightRadius: isMobileScreen ? '12px' : 0,
            backgroundColor: 'white',
            boxShadow: MAIN_BOX_SHADOW,
            mt: { mobile: 2, tablet: 0, desktop: 0 },
          }}
        >
          {content}
        </Box>
      )}
    </>
  );
};

export default Summary;
